'use client';
import { motion } from 'framer-motion';
import React, { useEffect, useRef, useState } from 'react';
import { Button } from 'utils/Button';
import ReusableSectionTwo from 'components/ReusableSectionTwo';
import { Helmet } from 'react-helmet-async';
import { LandingConsultation } from 'components/LandingConsultation';
import Breadcrumbs from 'components/Breadcrumbs';
import { useLocation } from 'react-router-dom';

const banner =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597767/oak-and-stone-remodel/bath/bath3.webp';
const intialConsultation =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597849/oak-and-stone-remodel/process/process-consultation.webp';
const designAndPlanning =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597852/oak-and-stone-remodel/process/process-schematic.webp';
const design2 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597833/oak-and-stone-remodel/process/design2.webp';
const design3 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597836/oak-and-stone-remodel/process/design3.webp';
const design4 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597837/oak-and-stone-remodel/process/design4.webp';
const material1 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597840/oak-and-stone-remodel/process/material1.webp';
const material2 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597844/oak-and-stone-remodel/process/material2.webp';
const material3 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597847/oak-and-stone-remodel/process/material3.webp';
const material4 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597848/oak-and-stone-remodel/process/material4.webp';
const demo1 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597829/oak-and-stone-remodel/process/demo1.webp';
const installation1 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597838/oak-and-stone-remodel/process/installation1.webp';
const installation2 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597852/oak-and-stone-remodel/process/process1.webp';
const walkthrough1 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597871/oak-and-stone-remodel/show/show1.webp';
const walkthrough2 =
  'https://res.cloudinary.com/dwoubdppz/image/upload/w_800,q_auto,f_auto/v1751597903/oak-and-stone-remodel/show/show6.webp';

export const Process = ({ data = [] }) => {
  const ref = useRef(null);
  const [, setHeight] = useState(0);
  const location = useLocation();

  useEffect(() => {
    if (ref.current) {
      const rect = ref.current.getBoundingClientRect();
      setHeight(rect.height);
    }
  }, [ref]);

  return (
    <>
      {/* Process Header */}
      <div className="relative min-h-[75vh] flex items-center justify-center">
        <Helmet>
          <title>Our Remodeling Process | Oak & Stone Construction</title>
          <meta
            name="description"
            content="Discover Oak & Stone Construction's streamlined home remodeling process. From consultation to final walkthrough, our expert team ensures a stress-free experience. Learn how we bring your vision to life."
          />
          {/* Open Graph Meta Tags */}
          <meta
            property="og:title"
            content="Our Remodeling Process | Oak & Stone Construction"
          />
          <meta
            property="og:description"
            content="Discover Oak & Stone Construction's streamlined home remodeling process. From consultation to final walkthrough, our expert team ensures a stress-free experience. Learn how we bring your vision to life."
          />
          <meta
            property="og:image"
            content="https://res.cloudinary.com/dwoubdppz/image/upload/v1751597767/oak-and-stone-remodel/bath/bath3.webp"
          />
          <meta
            property="og:url"
            content="https://oakandstoneremodel.com/process"
          />
          <meta property="og:type" content="website" />
          {/* Twitter Card Meta Tags */}
          <meta name="twitter:card" content="summary_large_image" />
          <meta
            name="twitter:title"
            content="Our Remodeling Process | Oak & Stone Construction"
          />
          <meta
            name="twitter:description"
            content="Discover Oak & Stone Construction's streamlined home remodeling process. From consultation to final walkthrough, our expert team ensures a stress-free experience. Learn how we bring your vision to life."
          />
          <meta
            name="twitter:image"
            content="https://res.cloudinary.com/dwoubdppz/image/upload/v1751597767/oak-and-stone-remodel/bath/bath3.webp"
          />
          <meta
            name="twitter:url"
            content="https://oakandstoneremodel.com/process"
          />
          <link rel="canonical" href="https://oakandstoneremodel.com/process" />
        </Helmet>
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'WebPage',
            name: 'Our Remodeling Process | Oak & Stone Construction',
            url: 'https://oakandstoneremodel.com/process',
            description:
              "Discover Oak & Stone Construction's streamlined home remodeling process. From consultation to final walkthrough, our expert team ensures a stress-free experience. Learn how we bring your vision to life."
          })}
        </script>

        <div className="absolute inset-0">
          <img
            src={banner}
            className="object-cover w-full h-full"
            alt="a kitchen remodel built by oak and stone in ogden utah, featuring beautiful custom cabinets and marble countertops"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/40 to-black/60"></div>
          <Breadcrumbs pathname={location.pathname} />
        </div>

        <div className="relative z-10 px-6 py-20 mx-auto w-full max-w-7xl">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="space-y-8 text-center">
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-4xl font-bold tracking-wide text-white uppercase md:text-5xl lg:text-6xl">
              Our Remodeling Process
            </motion.h1>

            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="mx-auto w-32 h-1 bg-blue-500"></motion.div>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="mx-auto max-w-3xl text-lg font-light leading-relaxed md:text-xl text-white/90">
              From initial consultation to final walkthrough, discover how we
              transform your vision into reality with our proven eight-step
              process.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="flex flex-wrap gap-4 justify-center mt-8">
              <Button
                to="/gallery"
                text="VIEW OUR WORK"
                classname="text-gray-900 bg-white transition-all duration-300 transform hover:bg-gray-100 hover:scale-105"
              />
              <Button
                to="/contact"
                text="FREE CONSULTATION"
                classname="text-white bg-primary transition-all duration-300 transform hover:bg-primary-hover hover:scale-105"
              />
            </motion.div>
          </motion.div>
        </div>
      </div>

      <ReusableSectionTwo
        title="Our Process"
        subtitle="How We Do It"
        content={[
          "At Oak and Stone, we've spent 35 years perfecting a remodeling process that's seamless and stress-free. We know transforming your home can feel daunting, but with our team by your side, you'll have expert guidance every step of the way. From answering your questions to offering creative solutions, we make sure your vision is front and center.",
          "Our craftsmanship speaks for itself, blending timeless design with exceptional quality. Each cabinet is carefully built to last, combining beauty and functionality to elevate your home. With Oak and Stone, you are not just remodeling. You are investing in a space that feels uniquely yours. Let's build something extraordinary together."
        ]}
        buttonText="Request Consultation"
        buttonLink="/contact"
      />

      <div className="relative px-6 py-20 bg-gradient-to-b from-gray-50 to-white">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-7xl">
          <div className="mb-12 text-center">
            <h2 className="mb-4 text-3xl font-bold text-gray-900">
              Stay Connected Throughout Your Project
            </h2>
            <div className="mx-auto mb-6 w-24 h-1 bg-blue-500"></div>
            <p className="mx-auto max-w-3xl text-lg text-gray-600">
              We use <span className="font-semibold">JobTread</span> project
              management software to keep you informed and involved at every
              step. Track progress, manage documents, and communicate with our
              team, all in one place.
            </p>
          </div>

          <div className="grid gap-12 items-center md:grid-cols-2">
            <div className="space-y-8">
              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="p-6 bg-white rounded-lg shadow-lg transition-shadow duration-300 hover:shadow-xl">
                <h3 className="mb-3 text-xl font-semibold text-gray-900">
                  Real-Time Updates
                </h3>
                <p className="text-gray-600">
                  Stay informed with instant notifications about project
                  milestones, schedule changes, and important updates.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="p-6 bg-white rounded-lg shadow-lg transition-shadow duration-300 hover:shadow-xl">
                <h3 className="mb-3 text-xl font-semibold text-gray-900">
                  Budget Tracking
                </h3>
                <p className="text-gray-600">
                  Monitor project expenses, review and approve change orders,
                  and maintain complete financial transparency.
                </p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, x: -20 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.3 }}
                className="p-6 bg-white rounded-lg shadow-lg transition-shadow duration-300 hover:shadow-xl">
                <h3 className="mb-3 text-xl font-semibold text-gray-900">
                  Document Management
                </h3>
                <p className="text-gray-600">
                  Access all project documents, contracts, and specifications in
                  one secure location.
                </p>
              </motion.div>
            </div>

            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="relative aspect-video">
              <iframe
                className="w-full h-full rounded-lg shadow-xl"
                src="https://www.youtube.com/embed/yOh3R7Ts7dE"
                title="JobTread Customer Portal Overview"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen></iframe>
            </motion.div>
          </div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.8 }}
            className="mt-12 text-center">
            <a
              href="https://www.jobtread.com/resources/help/customer-portal"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center px-8 py-3 text-lg font-medium text-white bg-primary rounded-md shadow-md transition-all duration-300 transform hover:bg-primary-hover hover:scale-105 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2">
              Get Started with JobTread
              <svg
                className="-mr-1 ml-2 w-5 h-5"
                fill="currentColor"
                viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z"
                  clipRule="evenodd"
                />
              </svg>
            </a>
          </motion.div>
        </motion.div>
      </div>

      {/* Timeline */}
      <div className="py-20 bg-white">
        <div className="px-6 mx-auto mb-16 max-w-7xl text-center">
          <h2 className="mb-4 text-3xl font-bold text-gray-900 md:text-4xl">
            Our Eight-Step Process
          </h2>
          <div className="mx-auto mb-6 w-24 h-1 bg-blue-500"></div>
          <p className="mx-auto max-w-3xl text-lg text-gray-600">
            We've refined our process over 35 years to ensure a seamless
            experience from start to finish. Each step is carefully designed to
            bring your vision to life.
          </p>
        </div>

        <div className="px-6 mx-auto space-y-16 w-full max-w-7xl">
          {processData.map((item, index) => (
            <div
              key={index}
              className="flex flex-col items-start p-8 bg-white rounded-xl shadow-lg md:flex-row md:gap-10">
              <div className="flex flex-row items-center mb-6 md:flex-col md:items-start md:w-1/5 md:mb-0">
                <div className="flex justify-center items-center mb-4 w-12 h-12 text-2xl font-bold text-white bg-primary rounded-full md:mb-8">
                  {index + 1}
                </div>
                <h3 className="pl-4 text-xl font-bold text-gray-900 md:text-2xl md:pl-0">
                  {item.title}
                </h3>
              </div>
              <div className="flex-1 w-full">{item.content}</div>
            </div>
          ))}
        </div>
      </div>

      {/* consultation banner */}
      <LandingConsultation />
    </>
  );
};

const processData = [
  {
    title: 'Initial Consultation',
    content: (
      <div>
        <p className="mb-8 text-lg leading-relaxed text-gray-700">
          We begin every project with an initial consultation at your home. We
          gather on-site measurements and discuss what you want for your space.
          If you already have measurements, or if the project is small (such as a
          single vanity), we can sometimes handle this step electronically
          instead.
        </p>
        <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
          <img
            src={intialConsultation}
            className="object-cover w-full h-64 rounded-lg shadow-md"
            alt="Initial consultation with our team"
            loading="lazy"
          />
          <div className="flex flex-col justify-center space-y-4">
            <h4 className="mb-4 text-xl font-semibold text-gray-900">
              What to Expect:
            </h4>
            <ul className="space-y-3 text-gray-700">
              <li className="flex items-center">
                <svg
                  className="mr-2 w-5 h-5 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                Discussion of your goals and preferences
              </li>
              <li className="flex items-center">
                <svg
                  className="mr-2 w-5 h-5 text-blue-600"
                  fill="currentColor"
                  viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
                On-site measurements of your space
              </li>
            </ul>
          </div>
        </div>
      </div>
    )
  },
  {
    title: 'Design and Planning',
    content: (
      <div className="grid gap-8 md:grid-cols-2">
        <div>
          <p className="text-lg text-gray-900 md:text-xl">
            Next, we schedule a Zoom call to review your design together. We
            walk you through a floor plan and 3D rendering, and we can update
            the layout live during the call.
          </p>
          <ul className="mt-6 space-y-2 list-disc list-inside text-gray-900 md:text-lg">
            <li>Zoom design review session</li>
            <li>Floor plan and 3D rendering walkthrough</li>
            <li>Live layout updates during the call</li>
          </ul>
        </div>
        <div className="grid grid-cols-2 gap-4">
          <img
            src={designAndPlanning}
            className="object-cover rounded-lg"
            alt="Design Mockup 1"
            loading="lazy"
          />
          <img
            src={design2}
            className="object-cover rounded-lg"
            alt="Design Mockup 2"
            loading="lazy"
          />
          <img
            src={design3}
            className="object-cover rounded-lg"
            alt="Design Mockup 3"
            loading="lazy"
          />
          <img
            src={design4}
            className="object-cover rounded-lg"
            alt="Design Mockup 4"
            loading="lazy"
          />
        </div>
      </div>
    )
  },
  {
    title: 'Estimate & Design Review',
    content: (
      <div className="grid gap-8 md:grid-cols-2">
        <div>
          <p className="text-lg text-gray-900 md:text-xl">
            After the design and planning meeting, we schedule another Zoom call
            to go over your estimate and rough budget, along with any updates to
            the design.
          </p>
          <ul className="mt-6 space-y-2 list-disc list-inside text-gray-900 md:text-lg">
            <li>Review of estimate and rough budget</li>
            <li>Design updates based on your feedback</li>
            <li>Alignment before moving into material selection</li>
          </ul>
        </div>
        <img
          src={design2}
          className="object-cover w-full h-full min-h-[16rem] rounded-lg"
          alt="Estimate and design review meeting"
          loading="lazy"
        />
      </div>
    )
  },
  {
    title: 'Material Selection',
    content: (
      <div className="space-y-8">
        <p className="text-lg text-center text-gray-900 md:text-xl">
          We guide you through the process of selecting high-quality materials
          that suit your design and budget.
        </p>
        <div className="grid grid-cols-2 gap-6 md:grid-cols-4">
          <div className="text-center">
            <img
              src={material1}
              className="object-cover rounded-lg"
              alt="Wood Types"
              loading="lazy"
            />
            <p className="mt-2 text-sm text-gray-600">Wood Types</p>
          </div>
          <div className="text-center">
            <img
              src={material2}
              className="object-cover rounded-lg"
              alt="Hardware"
              loading="lazy"
            />
            <p className="mt-2 text-sm text-gray-600">Hardware</p>
          </div>
          <div className="text-center">
            <img
              src={material3}
              className="object-cover rounded-lg"
              alt="Color Palette"
              loading="lazy"
            />
            <p className="mt-2 text-sm text-gray-600">Color Palette</p>
          </div>
          <div className="text-center">
            <img
              src={material4}
              className="object-cover rounded-lg"
              alt="Finishes"
              loading="lazy"
            />
            <p className="mt-2 text-sm text-gray-600">Finishes</p>
          </div>
        </div>
      </div>
    )
  },
  {
    title: 'Scheduling & Refined Proposal',
    content: (
      <div className="grid gap-8 md:grid-cols-2">
        <img
          src={design3}
          className="object-cover w-full h-full min-h-[16rem] rounded-lg md:order-1"
          alt="Project schedule and proposal review"
          loading="lazy"
        />
        <div className="md:order-2">
          <p className="text-lg text-gray-900 md:text-xl">
            Once materials are selected, we create a schedule for your project
            and present your updated, refined proposal so you know what to
            expect before construction begins.
          </p>
          <ul className="mt-6 space-y-2 list-disc list-inside text-gray-900 md:text-lg">
            <li>Project schedule creation</li>
            <li>Updated and refined proposal presentation</li>
            <li>Clear timeline before work starts</li>
          </ul>
        </div>
      </div>
    )
  },
  {
    title: 'Demolition and Site Preparation',
    content: (
      <div className="flex flex-col gap-8 items-start md:flex-row">
        <div className="md:w-3/5">
          <p className="mb-4 text-lg text-gray-900 md:text-xl">
            Once the design and materials are finalized, we carefully prepare
            the site by removing old cabinets or fixtures and ensuring the
            workspace is clean and ready for installation.
          </p>
          <h4 className="mb-4 text-lg font-bold text-gray-800">Steps:</h4>
          <ul className="space-y-2 list-decimal list-inside text-gray-900">
            <li>Clear out the work area</li>
            <li>Carefully remove existing structures</li>
            <li>Prepare surfaces for installation</li>
          </ul>
        </div>
        <img
          src={demo1}
          className="object-cover rounded-lg md:w-2/5"
          alt="Demolition"
          loading="lazy"
        />
      </div>
    )
  },
  {
    title: 'Construction & Installation',
    content: (
      <div className="space-y-8">
        <p className="text-lg text-gray-900 md:text-xl">
          Our expert team handles the full remodel. This
          includes custom cabinetry along with the other standard parts of the
          project, such as countertops, fixtures, flooring, tile, and finish
          work. Everything is installed with precision and care.
        </p>
        <div className="grid gap-6 md:grid-cols-2">
          <img
            src={installation1}
            className="object-cover rounded-lg"
            alt="Remodel construction in progress"
            loading="lazy"
          />
          <img
            src={installation2}
            className="object-cover rounded-lg"
            alt="Finished remodel installation"
            loading="lazy"
          />
        </div>
        <ul className="space-y-2 list-disc list-inside text-gray-900">
          <li>Custom cabinets and built-ins</li>
          <li>Countertops, fixtures, flooring, and tile</li>
          <li>Final quality checks throughout installation</li>
        </ul>
      </div>
    )
  },

  {
    title: 'Quality Checks and Client Walkthrough',
    content: (
      <div className="space-y-8">
        <p className="text-lg text-gray-900 md:text-xl">
          We finish with a walkthrough to make sure you are happy with the
          final results.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <img
            src={walkthrough2}
            className="object-cover rounded-lg"
            alt="Walkthrough Step 2"
            loading="lazy"
          />
          <img
            src={walkthrough1}
            className="object-cover rounded-lg"
            alt="Walkthrough Step 1"
            loading="lazy"
          />
        </div>
        <ul className="space-y-2 list-disc list-inside text-gray-900">
          <li>Answer final questions</li>
          <li>Provide maintenance tips</li>
          <li>Gather feedback</li>
        </ul>
      </div>
    )
  }
];

export default function App() {
  return <Process data={processData} />;
}
